import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import StudentLayout from '../../components/StudentLayout';
import { 
  FolderOpen, Clock, CheckCircle, DollarSign, 
  Calendar, AlertCircle, TrendingUp, FileText,
  PlayCircle, Flag, Users
} from 'lucide-react';
import { format, formatDistance } from 'date-fns';

const StudentDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeProjects: 0,
    pendingTasks: 0,
    completedProjects: 0,
    totalPaid: 0,
    pendingPayments: 0
  });
  const { user } = useAuth();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/api/projects/student');
      setProjects(response.data);
      calculateStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (projectsData) => {
    const activeProjects = projectsData.filter(p => 
      ['pending', 'in-progress', 'review'].includes(p.status)
    ).length;
    
    const completedProjects = projectsData.filter(p => p.status === 'completed').length;
    
    const pendingTasks = projectsData.reduce((total, project) => {
      return total + (project.tasks?.filter(t => t.status !== 'completed').length || 0);
    }, 0);

    const totalPaid = projectsData.reduce((total, project) => {
      return total + (project.payments?.filter(p => p.status === 'verified')
        .reduce((sum, payment) => sum + payment.amount, 0) || 0);
    }, 0);

    const pendingPayments = projectsData.reduce((total, project) => {
      return total + (project.payments?.filter(p => p.status === 'pending').length || 0);
    }, 0);

    setStats({
      activeProjects,
      pendingTasks,
      completedProjects,
      totalPaid,
      pendingPayments
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'on-hold': 'bg-gray-100 text-gray-800',
      'review': 'bg-purple-100 text-purple-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getRecentActivity = () => {
    const activities = [];
    
    projects.forEach(project => {
      // Recent tasks
      project.tasks?.forEach(task => {
        if (task.updatedAt) {
          activities.push({
            type: 'task',
            message: `Task "${task.title}" ${task.status}`,
            project: project.title,
            date: task.updatedAt
          });
        }
      });

      // Recent payments
      project.payments?.forEach(payment => {
        if (payment.paymentDate) {
          activities.push({
            type: 'payment',
            message: `${payment.type} payment ${payment.status}`,
            project: project.title,
            date: payment.paymentDate
          });
        }
      });
    });

    return activities
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);
  };

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-800">
            Welcome back, {user?.name}!
          </h1>
          <div className="text-sm text-gray-500">
            {format(new Date(), 'EEEE, MMMM dd, yyyy')}
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Active Projects</p>
                <p className="text-3xl font-bold">{stats.activeProjects}</p>
              </div>
              <FolderOpen className="w-8 h-8 text-blue-200" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Pending Tasks</p>
                <p className="text-3xl font-bold">{stats.pendingTasks}</p>
              </div>
              <Clock className="w-8 h-8 text-orange-200" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Completed</p>
                <p className="text-3xl font-bold">{stats.completedProjects}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-200" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Total Paid</p>
                <p className="text-2xl font-bold">LKR {stats.totalPaid.toLocaleString()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-purple-200" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl shadow-lg p-6 text-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Pending Payments</p>
                <p className="text-3xl font-bold">{stats.pendingPayments}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-200" />
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Current Projects */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-800">Current Projects</h3>
                <Link 
                  to="/student/projects"
                  className="text-primary-600 hover:text-primary-700 text-sm"
                >
                  View All →
                </Link>
              </div>

              <div className="space-y-4">
                {projects.slice(0, 3).map((project, index) => (
                  <motion.div
                    key={project._id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-semibold text-gray-800">{project.title}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(project.status)}`}>
                            {project.status}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm mb-2">{project.description}</p>
                        <div className="flex items-center text-xs text-gray-500 space-x-4">
                          <span className="flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            Due: {format(new Date(project.endDate), 'MMM dd')}
                          </span>
                          <span className="flex items-center">
                            <DollarSign className="w-3 h-3 mr-1" />
                            LKR {project.totalAmount?.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <Link
                        to={`/student/projects/${project._id}`}
                        className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                      >
                        View Details
                      </Link>
                    </div>
                  </motion.div>
                ))}

                {projects.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <FolderOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No projects assigned yet</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Recent Activity */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-6">Recent Activity</h3>
              
              <div className="space-y-4">
                {getRecentActivity().map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      activity.type === 'task' ? 'bg-blue-500' : 'bg-green-500'
                    }`} />
                    <div className="flex-1">
                      <p className="text-sm text-gray-800">{activity.message}</p>
                      <p className="text-xs text-gray-500">{activity.project}</p>
                      <p className="text-xs text-gray-400">
                        {formatDistance(new Date(activity.date), new Date(), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                ))}

                {getRecentActivity().length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <TrendingUp className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No recent activity</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </StudentLayout>
  );
};

export default StudentDashboard;